// src/components/TwitchEmbed.jsx
// Embedded Twitch player (and optional chat) for the Events page.
//
// Twitch requires a `parent` query param naming the domain doing the embedding,
// otherwise the player refuses to load. Deriving it from window.location.hostname
// means this works on localhost, Netlify preview builds and production without
// anyone maintaining a list of domains.

import { useEffect, useState } from "react";
import { FaTwitch, FaExternalLinkAlt } from "react-icons/fa";

function TwitchEmbed({ channel, title = "Live Now", subtitle = "", showChat = true }) {
  const [host, setHost] = useState(null);

  useEffect(() => {
    if (typeof window !== "undefined") setHost(window.location.hostname);
  }, []);

  if (!channel) return null;

  const playerSrc = host
    ? `https://player.twitch.tv/?channel=${encodeURIComponent(channel)}&parent=${host}&muted=false`
    : null;
  const chatSrc = host
    ? `https://www.twitch.tv/embed/${encodeURIComponent(channel)}/chat?parent=${host}&darkpopout`
    : null;

  return (
    <section className="px-6 pb-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-600 text-white text-xs font-black uppercase tracking-widest rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
              Live
            </span>
            <div>
              <h2 className="text-xl md:text-2xl font-black uppercase tracking-tight text-white leading-none">
                {title}
              </h2>
              {subtitle && <p className="text-sm text-gray-400 mt-1">{subtitle}</p>}
            </div>
          </div>
          <a
            href={`https://www.twitch.tv/${channel}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600/90 hover:bg-purple-600 text-white text-xs font-bold uppercase tracking-wide rounded-lg transition-colors"
          >
            <FaTwitch size={12} /> Watch on Twitch <FaExternalLinkAlt size={9} />
          </a>
        </div>

        {/* Player + chat */}
        <div className={`grid gap-3 ${showChat ? "lg:grid-cols-[1fr_340px]" : ""}`}>
          <div className="relative w-full aspect-video bg-black rounded-xl overflow-hidden border border-gray-800">
            {playerSrc ? (
              <iframe
                key={playerSrc}
                src={playerSrc}
                title={`${channel} live stream`}
                allowFullScreen
                frameBorder="0"
                scrolling="no"
                className="absolute inset-0 w-full h-full"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-gray-600 text-sm">
                Loading stream…
              </div>
            )}
          </div>

          {showChat && (
            // Chat is hidden on small screens — it's unusable at phone width and
            // the viewer can tap through to Twitch instead.
            <div className="hidden lg:block h-[480px] bg-black rounded-xl overflow-hidden border border-gray-800">
              {chatSrc && (
                <iframe
                  key={chatSrc}
                  src={chatSrc}
                  title={`${channel} chat`}
                  frameBorder="0"
                  scrolling="no"
                  className="w-full h-full"
                />
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default TwitchEmbed;
